
import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/AuthContext";
import { LOGIN_REDIRECT_PATH } from "@/constants/routes";
import { useEffect } from "react";

interface SupabaseAuthGuardProps {
  children: React.ReactNode;
  requireAuth?: boolean;
  requireRole?: "admin" | "ouvrier";
}

export const SupabaseAuthGuard: React.FC<SupabaseAuthGuardProps> = ({ 
  children, 
  requireAuth = true,
  requireRole 
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, profile, loading, error } = useAuth();

  useEffect(() => {
    if (loading) {
      console.log('⏳ [AuthGuard] Loading...');
      return;
    }

    const currentPath = location.pathname;
    console.log(`🛡️ [AuthGuard] Path: ${currentPath}, RequireAuth: ${requireAuth}, User: ${!!user}, Profile: ${profile?.role || 'none'}`);
    
    if (error) {
      console.log('❌ [AuthGuard] Error:', error);
      if (currentPath !== "/") {
        navigate("/", { replace: true });
      }
      return;
    }

    // Page de connexion (requireAuth = false)
    if (!requireAuth) {
      if (user && profile && currentPath === "/") {
        console.log(`✅ [AuthGuard] Redirecting authenticated user to ${LOGIN_REDIRECT_PATH}`);
        navigate(LOGIN_REDIRECT_PATH, { replace: true });
      }
      return;
    }

    // Pages protégées (requireAuth = true)
    if (!user) {
      console.log('❌ [AuthGuard] Not authenticated, redirecting to login');
      if (currentPath !== "/") {
        navigate("/", { replace: true });
      }
      return;
    }

    if (user && !profile) {
      console.log('⏳ [AuthGuard] Waiting for profile...');
      return;
    }

    if (requireRole && profile && profile.role !== requireRole) {
      console.log(`⚠️ [AuthGuard] Role mismatch: required=${requireRole}, actual=${profile.role}`);
      navigate(LOGIN_REDIRECT_PATH, { replace: true });
      return;
    }

    console.log('✅ [AuthGuard] Access granted');
  }, [loading, user, profile, requireAuth, requireRole, location.pathname, navigate, error]);

  // Toujours appeler tous les hooks - pas de retours anticipés
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F8F8] flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#BD1E28] border-e-transparent mb-4"></div>
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#F8F8F8] flex items-center justify-center">
        <div className="text-center bg-red-50 p-6 rounded-lg max-w-md">
          <p className="text-red-600 mb-4">Erreur d'authentification</p>
          <p className="text-sm text-gray-600 mb-4">{error}</p>
          <button 
            onClick={() => window.location.href = '/'} 
            className="bg-[#BD1E28] text-white px-4 py-2 rounded hover:bg-[#a01820]"
          >
            Retour à la connexion
          </button>
        </div>
      </div>
    );
  }

  // Pages publiques
  if (!requireAuth) {
    return <>{children}</>;
  }

  // Pages protégées - vérifications finales
  if (!user) {
    return (
      <div className="min-h-screen bg-[#F8F8F8] flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#BD1E28] border-e-transparent mb-4"></div>
          <p className="text-gray-600">Redirection...</p>
        </div>
      </div>
    );
  }

  if (user && !profile) {
    return (
      <div className="min-h-screen bg-[#F8F8F8] flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#BD1E28] border-e-transparent mb-4"></div>
          <p className="text-gray-600">Chargement du profil...</p>
        </div>
      </div>
    );
  }

  if (requireRole && profile && profile.role !== requireRole) {
    return (
      <div className="min-h-screen bg-[#F8F8F8] flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#BD1E28] border-e-transparent mb-4"></div>
          <p className="text-gray-600">Redirection...</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};
